import {
    h,
    FunctionalComponent,
    ComponentChildren,
    createContext,
} from "preact";
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import createStore, { Store } from "unistore";
import { Provider } from "unistore/preact";
import devtools from "unistore/devtools";
import Actions from "./actions";

export interface StoreContextWrapperProps {
    children?: ComponentChildren;
}

export interface StoreContextState {
    apiBasePath?: string;
    token: string;
    isLoading: boolean;
    pendingRequests: string[];
    initialized: boolean;
    error: unknown;
}

/**
 * Set initial state
 * @type {Object}
 */
const initialState: StoreContextState = {
    initialized: false,
    apiBasePath: "",
    token: "",
    isLoading: false,
    pendingRequests: [],
    error: "",
};

/**
 * Create store object
 * @type {Store}
 */
export const createSDKStore = () =>
    !__SDK_DEV__
        ? createStore<StoreContextState>({
              ...initialState,
              pendingRequests: [],
          })
        : devtools(
              createStore<StoreContextState>({
                  ...initialState,
                  pendingRequests: [],
              }),
          );

const SDKStoreContext = createContext<Store<StoreContextState> | null>(null);

export const useLoadingStore = () => {
    const store = useContext(SDKStoreContext);
    if (!store) throw new Error("SDK store provider is missing");
    const [isLoading, setIsLoading] = useState(store.getState().isLoading);
    useEffect(() => {
        const update = (state: StoreContextState) =>
            setIsLoading(state.isLoading);
        const unsubscribe = store.subscribe(update);
        update(store.getState());
        return unsubscribe;
    }, [store]);
    const actions = useMemo(() => {
        const actions = Actions();
        return {
            startLoading: (id: string) =>
                store.action(actions.startLoading)(id),
            stopLoading: (id: string) => store.action(actions.stopLoading)(id),
            setApiBasePath: (path: string) =>
                store.action(actions.setApiBasePath)(path),
        };
    }, [store]);
    return { isLoading, ...actions };
};

const StoreContextWrapper: FunctionalComponent<StoreContextWrapperProps> = ({
    children,
}: StoreContextWrapperProps) => {
    const [store] = useState(createSDKStore);
    return (
        <SDKStoreContext.Provider value={store}>
            <Provider store={store}>{children}</Provider>
        </SDKStoreContext.Provider>
    );
};

export { StoreContextWrapper, Actions };
